// src/pages/Admin/Business/Finance/modals/DeadlineModal.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaBuilding,
  FaCalendarAlt,
  FaCheckCircle,
  FaPlusCircle,
  FaSave,
  FaTag,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Button from "../../../../../../components/Button/Button";
import { getAllAssociations } from "../../../../../../services/associationsServices";
import {
  addEcheance,
  updateEcheance,
} from "../../../../../../services/echeanceServices";
import {
  addMonths,
  parseISODate,
  toInputDate,
} from "../financeCommitments.utils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-indigo-900/40";

function buildInitialForm(deadline) {
  const today = new Date();
  const nextYear = addMonths(today, 12);

  return {
    asso_id: deadline?.asso_id || "",
    nom_echeance: deadline?.nom_echeance || "",
    categorie_echeance: deadline?.categorie_echeance || "",
    debut_echeance: toInputDate(deadline?.debut_echeance) || toInputDate(today),
    fin_echeance: toInputDate(deadline?.fin_echeance) || toInputDate(nextYear),
    renouvellement_auto: Boolean(deadline?.renouvellement_auto),
  };
}

const DeadlineModal = ({ isOpen, onClose, deadline, refreshList }) => {
  const isEdit = Boolean(deadline?.id);

  const [formData, setFormData] = useState(buildInitialForm(deadline));
  const [associations, setAssociations] = useState([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(buildInitialForm(deadline));

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, deadline, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchAssociations = async () => {
      try {
        setLoadingAssociations(true);
        const data = await getAllAssociations();
        setAssociations(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error("Erreur lors du chargement des associations.");
      } finally {
        setLoadingAssociations(false);
      }
    };

    fetchAssociations();
  }, [isOpen]);

  const selectedAssociationName = useMemo(() => {
    const found = associations.find(
      (asso) => String(asso.id) === String(formData.asso_id)
    );
    return found?.nom_asso || "Aucune association sélectionnée";
  }, [associations, formData.asso_id]);

  const durationLabel = useMemo(() => {
    const start = parseISODate(formData.debut_echeance);
    const end = parseISODate(formData.fin_echeance);
    if (!start || !end) return "—";

    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (!Number.isFinite(diff)) return "—";
    return `${diff} jour${diff > 1 ? "s" : ""}`;
  }, [formData.debut_echeance, formData.fin_echeance]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleQuickDuration = (months) => {
    const start = parseISODate(formData.debut_echeance);
    if (!start) return;

    const next = addMonths(start, months);
    setFormData((prev) => ({
      ...prev,
      fin_echeance: toInputDate(next),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.asso_id ||
      !formData.nom_echeance ||
      !formData.categorie_echeance ||
      !formData.debut_echeance ||
      !formData.fin_echeance
    ) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }

    try {
      setSaving(true);

      if (isEdit) {
        await updateEcheance(deadline.id, formData);
        toast.success("Échéance modifiée avec succès.");
      } else {
        await addEcheance(formData);
        toast.success("Échéance ajoutée avec succès.");
      }

      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement de l’échéance.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200]">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-[3px]"
        onClick={onClose}
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_90px_-30px_rgba(0,0,0,0.45)] dark:bg-slate-900">
          <div className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.26),transparent_28%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.22),transparent_24%),linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#312e81_100%)]" />
            <div className="relative px-5 py-5 sm:px-6 sm:py-6 text-white">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                    <FaPlusCircle />
                    {isEdit ? "Modification" : "Nouvelle échéance"}
                  </div>

                  <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                    {isEdit ? "Modifier une échéance" : "Ajouter une échéance"}
                  </h3>

                  <p className="mt-1 max-w-2xl text-sm text-white/75">
                    Crée une échéance future, avec période et option de
                    renouvellement automatique.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950/60">
            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                <div className="xl:col-span-8 space-y-6">
                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Association & échéance"
                      subtitle="Libellé et catégorie à surveiller."
                    />

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Field>
                        <Label required>Association</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaBuilding />
                          </span>
                          <select
                            className={`${inputClass} pl-10`}
                            name="asso_id"
                            value={formData.asso_id}
                            onChange={handleChange}
                            required
                          >
                            <option value="">
                              {loadingAssociations ? "Chargement..." : "Sélectionnez une association"}
                            </option>
                            {associations.map((asso) => (
                              <option key={asso.id} value={asso.id}>
                                {asso.nom_asso}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Field>

                      <Field>
                        <Label required>Nom de l’échéance</Label>
                        <input
                          className={inputClass}
                          type="text"
                          name="nom_echeance"
                          value={formData.nom_echeance}
                          onChange={handleChange}
                          placeholder="Ex : Nom de domaine monappli.club"
                          required
                        />
                      </Field>

                      <Field>
                        <Label required>Catégorie</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaTag />
                          </span>
                          <input
                            className={`${inputClass} pl-10`}
                            type="text"
                            name="categorie_echeance"
                            value={formData.categorie_echeance}
                            onChange={handleChange}
                            placeholder="Ex : Nom de domaine"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label>Renouvellement automatique</Label>
                        <label className="flex h-[52px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 dark:border-slate-700 dark:bg-slate-800">
                          <input
                            type="checkbox"
                            name="renouvellement_auto"
                            checked={formData.renouvellement_auto}
                            onChange={handleChange}
                            className="h-4 w-4 rounded"
                          />
                          <span className="text-sm text-slate-700 dark:text-slate-200">
                            Cette échéance se renouvelle automatiquement
                          </span>
                        </label>
                      </Field>
                    </div>
                  </section>

                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Période"
                      subtitle="Début, fin et raccourcis de durée."
                    />

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Field>
                        <Label required>Date de début</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaCalendarAlt />
                          </span>
                          <input
                            className={`${inputClass} pl-10`}
                            type="date"
                            name="debut_echeance"
                            value={formData.debut_echeance}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Date de fin</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaCalendarAlt />
                          </span>
                          <input
                            className={`${inputClass} pl-10`}
                            type="date"
                            name="fin_echeance"
                            value={formData.fin_echeance}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </Field>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <QuickButton onClick={() => handleQuickDuration(1)}>+ 1 mois</QuickButton>
                      <QuickButton onClick={() => handleQuickDuration(3)}>+ 3 mois</QuickButton>
                      <QuickButton onClick={() => handleQuickDuration(6)}>+ 6 mois</QuickButton>
                      <QuickButton onClick={() => handleQuickDuration(12)}>+ 12 mois</QuickButton>
                    </div>
                  </section>
                </div>

                <div className="xl:col-span-4 space-y-6">
                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Résumé"
                      subtitle="Aperçu rapide avant validation."
                    />

                    <div className="mt-5 space-y-4">
                      <SummaryRow
                        icon={<FaBuilding />}
                        label="Association"
                        value={selectedAssociationName}
                      />
                      <SummaryRow
                        icon={<FaTag />}
                        label="Catégorie"
                        value={formData.categorie_echeance || "Non renseignée"}
                      />
                      <SummaryRow
                        icon={<FaCalendarAlt />}
                        label="Début"
                        value={formData.debut_echeance || "—"}
                      />
                      <SummaryRow
                        icon={<FaCalendarAlt />}
                        label="Fin"
                        value={formData.fin_echeance || "—"}
                      />
                      <SummaryRow
                        icon={<FaCheckCircle />}
                        label="Durée"
                        value={durationLabel}
                      />
                      <SummaryRow
                        icon={<FaCheckCircle />}
                        label="Auto-renouvellement"
                        value={formData.renouvellement_auto ? "Oui" : "Non"}
                      />
                    </div>
                  </section>
                </div>
              </div>
            </form>
          </div>

          <div className="border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <Button
                text="Annuler"
                icon={FaTimes}
                onClick={onClose}
                className="gap-2 !rounded-2xl"
              />
              <Button
                text={saving ? "Enregistrement..." : isEdit ? "Enregistrer" : "Ajouter l’échéance"}
                icon={FaSave}
                type="submit"
                onClick={handleSubmit}
                disabled={saving}
                className="gap-2 !rounded-2xl"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function SectionTitle({ title, subtitle }) {
  return (
    <div>
      <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h4>
      {subtitle ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function Field({ children }) {
  return <div>{children}</div>;
}

function Label({ children, required = false }) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          {label}
        </div>
        <div className="mt-1 break-words text-sm font-medium text-slate-900 dark:text-slate-50">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

function QuickButton({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
    >
      {children}
    </button>
  );
}

export default DeadlineModal;